import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Clock, Camera, Send, Loader2, MapPin, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { jobCategoryOptions, jobCategoryLabel } from '@/lib/jobCategory';

const API = window.location.origin;

export default function MobileWorksite() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const token = localStorage.getItem('emp_token');

  const [client, setClient] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clocking, setClocking] = useState(false);
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photos, setPhotos] = useState([]);
  // Job category for this punch. Pre-filled from existing activity (resume)
  // or from the client's default. Worker can still change before clocking in.
  const [jobCategory, setJobCategory] = useState('');

  useEffect(() => {
    if (!token) { navigate('/mobile'); return; }
    load();
  }, [clientId]);

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/mobile/worksites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { navigate('/mobile'); return; }
      const data = await res.json();
      const ws = data.worksites.find(w => w.client_id === clientId);
      if (ws) {
        setClient(ws);
        if (ws.today_activity) {
          setActivity(ws.today_activity);
          setNote(ws.today_activity.notes || '');
          // Load photos
          if (ws.today_activity.photos?.length) {
            setPhotos(ws.today_activity.photos);
          }
          // Existing punch: preserve its job_category
          setJobCategory(ws.today_activity.job_category || '');
        } else {
          // No punch yet: pre-select from the client's job_category if any
          setJobCategory(ws.job_category || '');
        }
      }
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  const getGeoLocation = () => new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  });

  const clockIn = async () => {
    setClocking(true);
    try {
      const geo = await getGeoLocation();
      const res = await fetch(`${API}/api/mobile/clock-in`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clientId,
          job_category: jobCategory || null,
          ...(geo || {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      setActivity(data);
      toast.success('Arrivee pointee!');
    } catch (err) { toast.error(err.message); }
    finally { setClocking(false); }
  };

  const clockOut = async () => {
    if (!activity) return;
    setClocking(true);
    try {
      const geo = await getGeoLocation();
      const res = await fetch(`${API}/api/mobile/clock-out`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ activity_id: activity.activity_id, ...(geo || {}) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      setActivity(data);
      toast.success('Depart pointe!');
    } catch (err) { toast.error(err.message); }
    finally { setClocking(false); }
  };

  const saveNote = async () => {
    if (!activity) return;
    setSavingNote(true);
    try {
      const res = await fetch(`${API}/api/mobile/note`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ activity_id: activity.activity_id, note }),
      });
      if (!res.ok) throw new Error();
      toast.success('Note sauvegardee');
    } catch { toast.error('Erreur'); }
    finally { setSavingNote(false); }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activity) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        const res = await fetch(`${API}/api/mobile/photo`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ activity_id: activity.activity_id, photo_data: base64 }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Erreur');
        setPhotos(prev => [...prev, { photo_id: data.photo_id, url: data.url, uploaded_at: new Date().toISOString() }]);
        toast.success('Photo ajoutee!');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch { toast.error('Erreur upload'); setUploading(false); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const isClockedIn = activity && !activity.clock_out;
  const isCompleted = activity?.clock_out;

  const formatLocation = (loc) => {
    if (!loc) return null;
    return `${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)}`;
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mobile-worksite">
      {/* Header */}
      <div className="bg-orange-500 text-white px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/mobile/home')} className="text-white hover:bg-orange-600 h-9 w-9">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-base truncate">{client?.first_name} {client?.last_name}</p>
            {client?.address && (
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(client.address)}`}
                target="_blank" rel="noopener noreferrer"
                className="text-orange-100 text-xs flex items-center gap-1 truncate"
              >
                <MapPin className="h-3 w-3 shrink-0" />{client.address}
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 pb-20">
        {/* Clock In/Out */}
        <Card>
          <CardContent className="p-4 text-center space-y-3">
            {!activity ? (
              <>
                <div className="text-left">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Type de job
                  </label>
                  <select
                    value={jobCategory}
                    onChange={(e) => setJobCategory(e.target.value)}
                    disabled={clocking}
                    className="w-full h-11 px-3 rounded-lg border bg-background text-sm"
                    data-testid="punch-job-category-select"
                  >
                    <option value="">— Choisir —</option>
                    {jobCategoryOptions('fr').map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <Button
                  onClick={clockIn} disabled={clocking}
                  className="w-full h-14 bg-green-500 hover:bg-green-600 text-lg font-bold rounded-xl"
                  data-testid="clock-in-btn"
                >
                  {clocking ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                    <><Clock className="mr-2 h-5 w-5" />Pointer arrivee</>
                  )}
                </Button>
              </>
            ) : isClockedIn ? (
              <>
                {activity.job_category && (
                  <div className="text-left text-xs text-muted-foreground">
                    Type de job : <span className="font-medium text-foreground">{jobCategoryLabel(activity.job_category, 'fr')}</span>
                  </div>
                )}
                <div className="flex items-center justify-center gap-2 text-green-600 font-medium">
                  <CheckCircle2 className="h-4 w-4" />
                  Arrive a {new Date(activity.clock_in).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                </div>
                {activity.clock_in_location && (
                  <a
                    href={`https://maps.google.com/?q=${activity.clock_in_location.lat},${activity.clock_in_location.lng}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1 text-xs text-blue-500 hover:underline"
                    data-testid="clock-in-gps"
                  >
                    <MapPin className="h-3 w-3" />
                    GPS: {formatLocation(activity.clock_in_location)}
                  </a>
                )}
                <Button
                  onClick={clockOut} disabled={clocking}
                  className="w-full h-14 bg-red-500 hover:bg-red-600 text-lg font-bold rounded-xl"
                  data-testid="clock-out-btn"
                >
                  {clocking ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                    <><Clock className="mr-2 h-5 w-5" />Pointer depart</>
                  )}
                </Button>
              </>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-2 text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {new Date(activity.clock_in).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                    {' - '}
                    {new Date(activity.clock_out).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  {activity.clock_in_location && (
                    <a
                      href={`https://maps.google.com/?q=${activity.clock_in_location.lat},${activity.clock_in_location.lng}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-blue-500 hover:underline"
                      data-testid="clock-in-gps-done"
                    >
                      <MapPin className="h-3 w-3" />
                      Arrivee: {formatLocation(activity.clock_in_location)}
                    </a>
                  )}
                  {activity.clock_out_location && (
                    <a
                      href={`https://maps.google.com/?q=${activity.clock_out_location.lat},${activity.clock_out_location.lng}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-blue-500 hover:underline"
                      data-testid="clock-out-gps-done"
                    >
                      <MapPin className="h-3 w-3" />
                      Depart: {formatLocation(activity.clock_out_location)}
                    </a>
                  )}
                </div>
                <Badge className="bg-gray-100 text-gray-600">Termine</Badge>
                <Button
                  onClick={() => { setActivity(null); clockIn(); }}
                  disabled={clocking}
                  variant="outline"
                  className="w-full h-12 border-orange-300 text-orange-600 font-bold rounded-xl"
                  data-testid="reopen-btn"
                >
                  {clocking ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                    <><Clock className="mr-2 h-5 w-5" />Reprendre le pointage</>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Photos */}
        {activity && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-bold text-sm">Photos ({photos.length})</p>
                {(isClockedIn || isCompleted) && (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="add-photo-btn"
                  >
                    {uploading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Camera className="mr-1.5 h-3.5 w-3.5" />}
                    Ajouter
                  </Button>
                )}
              </div>
              <input
                ref={fileRef} type="file" accept="image/*" capture="environment"
                className="hidden" onChange={handlePhoto}
              />
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {photos.map(p => (
                    <img
                      key={p.photo_id}
                      src={`${API}${p.url || `/api/photos/file/${p.photo_id}`}`}
                      alt=""
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Note */}
        {activity && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <p className="font-bold text-sm">Note rapide</p>
              <Textarea
                value={note} onChange={e => setNote(e.target.value)}
                placeholder="Observations, details des travaux..."
                rows={3} className="text-base"
                data-testid="quick-note"
              />
              <Button
                onClick={saveNote} disabled={savingNote}
                variant="outline" className="w-full"
                data-testid="save-note-btn"
              >
                {savingNote ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Sauvegarder la note
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
